import { datadogLogs } from '@datadog/browser-logs';
import { isBotUserAgent } from '@/utils/botDetection';

const RELOAD_GUARD_KEY = 'inflcr-asset-reload-version';

let reloadScheduled = false;

/**
 * Hard-reloads at most once per app version per tab to recover from an asset that
 * failed to load. Keying on APP_VERSION means a tab left open through a deploy
 * still gets its reload, while a permanently unavailable asset gets exactly one.
 * Fails closed when sessionStorage is unavailable, so a loop is impossible.
 */
export const attemptRecoveryReload = (target?: string): boolean => {
	if (reloadScheduled) return true;

	try {
		if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === APP_VERSION) return false;
		window.sessionStorage.setItem(RELOAD_GUARD_KEY, APP_VERSION);
	} catch {
		return false;
	}

	reloadScheduled = true;
	if (target) window.location.assign(target);
	else window.location.reload();
	return true;
};

/**
 * Vite rejects a lazy chunk's promise when its preloaded stylesheet errors and
 * rethrows unless the cancelable vite:preloadError event is cancelled, so those
 * rejections reached window.onerror and Datadog (INF-3580).
 *
 * Must be registered before the first lazy chunk load — see main.ts. Returns a
 * function that removes the listener.
 */
export const registerPreloadErrorHandler = () => {
	const controller = new AbortController();

	window.addEventListener('vite:preloadError', (event) => {
		// Stylesheet failures only. Cancelling a JS module failure would make the
		// dynamic import resolve with undefined instead of rejecting, so
		// router.onError would never see it and its recovery would break.
		if (!event.payload?.message?.startsWith('Unable to preload CSS')) return;

		// Cancelled, so the module still resolves and the page renders unstyled
		// rather than throwing.
		event.preventDefault();

		if (isBotUserAgent()) return;
		if (attemptRecoveryReload()) return;

		datadogLogs.logger.warn('Stylesheet preload failed; continuing unstyled', {
			error: { message: event.payload.message, stack: event.payload.stack },
			url: window.location.href,
		});
	}, { signal: controller.signal });

	return () => controller.abort();
};
